import { useCallback, useMemo, useState } from 'react';
import { toUserMessage } from '@platform/api-client';
import { toKstDateTimeInput } from '@platform/datetime';
import type { JSONContent } from '@tiptap/react';
import { Button } from '@platform/ui';
import type { BoardType } from '@/shared/constants/app/routes.constant.ts';
import {
  postCommonSchema,
  noticeMetaSchema,
  updateMetaSchema,
  eventMetaSchema,
  faqMetaSchema,
  archiveMetaSchema,
} from '../schemas/post.schemas.ts';
import { useZodForm } from '../hooks/useZodForm.ts';
import { PostEditor } from './PostEditor.tsx';
import { AttachmentUploader } from './AttachmentUploader.tsx';
import { MetaFields } from './MetaFields.tsx';
import { FieldShell } from './FieldShell.tsx';
import { inputClass } from './fieldStyles.ts';
import { isEmptyDoc } from './editorExtensions.ts';
import type {
  ArchivePost,
  AttachmentInput,
  BoardConfig,
  CreatePostInput,
  FaqPost,
  Post,
} from '../types/board.type.ts';

interface BoardFormProps {
  type: BoardType;
  config: BoardConfig<Post>;
  /** 수정 시 기존 글. 생성이면 undefined */
  initial?: Post;
  /** 저장 핸들러 (생성/수정 mutation). 성공 시 부모가 라우팅. */
  onSubmit: (input: CreatePostInput) => Promise<void>;
  submitting: boolean;
  onCancel: () => void;
  /** 저장 실패 등 에러 메시지 표시 */
  onError?: (message: string) => void;
}

interface CommonValues extends Record<string, unknown> {
  title: string;
  excerpt: string;
  isPinned: boolean;
}

/** 게시판 타입 → meta 스키마 매핑 */
function getMetaSchema(type: BoardType) {
  switch (type) {
    case 'notices':
      return noticeMetaSchema;
    case 'updates':
      return updateMetaSchema;
    case 'events':
      return eventMetaSchema;
    case 'faqs':
      return faqMetaSchema;
    case 'archives':
      return archiveMetaSchema;
    default:
      return null;
  }
}

/** 기존 Post → meta 초기값 추출 */
function extractMeta(type: BoardType, post?: Post): Record<string, unknown> {
  if (!post) return {};
  if (type === 'notices' && 'category' in post) {
    return { category: post.category ?? '' };
  }
  if (type === 'updates' && 'productSlug' in post) {
    return { productSlug: post.productSlug, version: post.version ?? '' };
  }
  if (type === 'events' && 'startsAt' in post) {
    // datetime-local 은 KST 'YYYY-MM-DDTHH:mm' — 제출 시 eventMetaSchema 가 UTC ISO 로 되돌린다.
    return {
      startsAt: toKstDateTimeInput(post.startsAt),
      endsAt: toKstDateTimeInput(post.endsAt),
      applyUrl: post.applyUrl ?? '',
    };
  }
  if (type === 'faqs' && 'category' in post) {
    const faq = post as FaqPost;
    return {
      faqCategory: faq.category ?? '',
      faqSubCategory: faq.subCategory ?? '',
    };
  }
  if (type === 'archives' && 'category' in post) {
    const archive = post as ArchivePost;
    return {
      archiveCategory: archive.category ?? '',
      archiveSubCategory: archive.subCategory ?? '',
    };
  }
  return {};
}

/**
 * 작성/수정 공통 폼.
 *
 * 한 컴포넌트로 생성/수정을 모두 처리(initial 유무로 분기).
 * - 본문은 PostEditor 비제어 상태 → onChange 로 최신 JSON 만 보관.
 * - 공통 필드(title/excerpt/isPinned)는 useZodForm.
 * - meta(게시판별)는 별도 상태 + 타입별 zod 스키마로 제출 시 검증.
 * - 첨부는 업로드 완료 메타 목록만 보관, 저장 시 함께 전송.
 */
export function BoardForm({
  type,
  config,
  initial,
  onSubmit,
  submitting,
  onCancel,
  onError,
}: BoardFormProps) {
  const common = useZodForm<CommonValues>(
    postCommonSchema as never,
    {
      title: initial?.title ?? '',
      excerpt: initial?.excerpt ?? '',
      isPinned: initial?.isPinned ?? false,
    },
  );

  const [body, setBody] = useState<JSONContent | undefined>(initial?.body);
  const [bodyError, setBodyError] = useState<string>();
  const [meta, setMeta] = useState<Record<string, unknown>>(
    extractMeta(type, initial),
  );
  const [metaErrors, setMetaErrors] = useState<Record<string, string>>({});
  const [attachments, setAttachments] = useState<AttachmentInput[]>(
    (initial?.attachments ?? []).map((a) => ({
      s3Key: a.s3Key,
      fileName: a.fileName,
      fileSize: a.fileSize,
      mimeType: a.mimeType,
    })),
  );

  const metaSchema = useMemo(() => getMetaSchema(type), [type]);

  const setMetaField = useCallback((key: string, value: unknown) => {
    setMeta((prev) => ({ ...prev, [key]: value }));
    setMetaErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const handleSubmit = useCallback(async () => {
    // 1) 공통 필드 검증
    const commonOk = common.validate();
    // 2) 본문 검증
    const bodyOk = body && !isEmptyDoc(body);
    setBodyError(bodyOk ? undefined : '내용을 입력해주세요.');
    // 3) meta 검증
    let metaOk = true;
    let parsedMeta: Record<string, unknown> = {};
    if (metaSchema) {
      const result = metaSchema.safeParse(meta);
      if (result.success) {
        parsedMeta = result.data;
      } else {
        metaOk = false;
        const errs: Record<string, string> = {};
        for (const issue of result.error.issues) {
          const k = issue.path[0] as string | undefined;
          if (k && !errs[k]) errs[k] = issue.message;
        }
        setMetaErrors(errs);
      }
    }

    if (!commonOk || !bodyOk || !metaOk) return;

    const input: CreatePostInput = {
      title: common.values.title,
      body: body!,
      excerpt: common.values.excerpt || undefined,
      isPinned: common.values.isPinned,
      attachments,
      meta: parsedMeta,
    };

    try {
      await onSubmit(input);
    } catch (err) {
      onError?.(
        toUserMessage(err, '저장 중 오류가 발생했습니다.'),
      );
    }
  }, [common, body, meta, metaSchema, attachments, onSubmit, onError]);

  return (
    <div className="space-y-6">
      {/* 제목 */}
      <FieldShell label="제목" htmlFor="title" error={common.errors.title} required>
        <input
          id="title"
          type="text"
          value={common.values.title}
          onChange={(e) => common.setField('title', e.target.value)}
          placeholder="제목을 입력하세요"
          className={inputClass(common.errors.title)}
          maxLength={200}
        />
      </FieldShell>

      {/* 게시판별 meta */}
      <MetaFields
        type={type}
        config={config}
        meta={meta}
        errors={metaErrors}
        onChange={setMetaField}
      />

      {/* 요약 */}
      <FieldShell
        label="요약"
        htmlFor="excerpt"
        error={common.errors.excerpt}
        hint="목록에 노출됩니다. 비우면 본문 앞부분이 사용됩니다."
      >
        <textarea
          id="excerpt"
          rows={2}
          value={common.values.excerpt}
          onChange={(e) => common.setField('excerpt', e.target.value)}
          placeholder="한 줄 요약 (선택)"
          className={inputClass(common.errors.excerpt) + ' resize-none'}
          maxLength={300}
        />
      </FieldShell>

      {/* 본문 에디터 */}
      <FieldShell label="내용" error={bodyError} required>
        <PostEditor
          initialContent={initial?.body}
          onChange={setBody}
          onError={onError}
        />
      </FieldShell>

      {/* 첨부 */}
      <FieldShell label="첨부파일">
        <AttachmentUploader
          value={attachments}
          onChange={setAttachments}
          onError={onError}
        />
      </FieldShell>

      {/* 상단 고정 */}
      <label className="flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-300">
        <input
          type="checkbox"
          checked={common.values.isPinned}
          onChange={(e) => common.setField('isPinned', e.target.checked)}
          className="h-4 w-4 rounded border-black/20 dark:border-white/20"
        />
        목록 상단에 고정
      </label>

      {/* 액션 */}
      <div className="flex justify-end gap-2 border-t border-black/10 dark:border-white/10 pt-5">
        <Button variant="secondary" onClick={onCancel} disabled={submitting}>
          취소
        </Button>
        <Button onClick={handleSubmit} loading={submitting}>
          {initial ? '수정 완료' : '등록'}
        </Button>
      </div>
    </div>
  );
}
